import { type PasswordLimitsFragment } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { InvisibleIcon, ViewIcon } from '@blocksuite/icons/rc';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { z, type ZodCustomIssue, ZodIssueCode } from 'zod';

import { IconButton } from '../../../ui/button';
import type { InputProps } from '../../../ui/input';
import { Input } from '../../../ui/input';
import * as styles from '../share.css';
import { ErrorIcon } from './error';
import { statusWrapper, visibilityToggle } from './style.css';
import { SuccessIcon } from './success';
import { Tag } from './tag';

// SEC-001.1.1: NIST Policy - Only length matters, no complexity/strength indicators
export type Status = 'minimum' | 'maximum';

export const PasswordInput: FC<
  InputProps & {
    passwordLimits: PasswordLimitsFragment;
    onPass: (password: string) => void;
    onPrevent: () => void;
  }
> = ({ passwordLimits, onPass, onPrevent, autoFocus, ...inputProps }) => {
  const t = useI18n();

  const [status, setStatus] = useState<Status | null>(null);
  const [confirmStatus, setConfirmStatus] = useState<
    'success' | 'error' | null
  >(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [password, setPassWord] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const validationSchema = useMemo(() => {
    const { minLength, maxLength } = passwordLimits;
    return z.string().superRefine((val, ctx) => {
      if (val.length < minLength) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          params: {
            status: 'minimum',
          },
        });
      } else if (val.length > maxLength) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          params: {
            status: 'maximum',
          },
        });
      }
      // SEC-001.1.1: NIST Policy - No strength/complexity check, only length
    });
  }, [passwordLimits]);

  const validatePasswords = useCallback(
    (password: string, confirmPassword: string) => {
      const result = validationSchema.safeParse(password);
      // SEC-001.1.1: NIST Policy - Only length validation, no strength check
      const lengthValid = result.success;
      if (!lengthValid) {
        const issues = result.error.issues as ZodCustomIssue[];
        const firstIssue = issues[0];
        setStatus(firstIssue.params?.status || null);
      } else {
        setStatus(null);
      }

      let canSubmit = lengthValid;
      if (confirmPassword) {
        const isEqual = password === confirmPassword;
        setConfirmStatus(isEqual ? 'success' : 'error');
        canSubmit &&= isEqual;
      } else {
        canSubmit = false;
        setConfirmStatus(null);
      }
      setCanSubmit(canSubmit);
    },
    [validationSchema]
  );

  const onPasswordChange = useCallback(
    (value: string) => {
      const password = value.trim();
      setPassWord(password);
      validatePasswords(password, confirmPassword);
    },
    [validatePasswords, confirmPassword]
  );

  const onConfirmPasswordChange = useCallback(
    (value: string) => {
      const confirmPassword = value.trim();
      setConfirmPassword(confirmPassword);
      validatePasswords(password, confirmPassword);
    },
    [validatePasswords, password]
  );

  useEffect(() => {
    if (canSubmit) {
      onPass(password);
    } else {
      onPrevent();
    }
  }, [canSubmit, password, onPass, onPrevent]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  return (
    <form onSubmit={e => e.preventDefault()} autoComplete="off">
      <Input
        name="password"
        className={styles.input}
        type={showPassword ? 'text' : 'password'}
        size="extraLarge"
        autoComplete="new-password"
        autoFocus={autoFocus}
        style={{ marginBottom: 20 }}
        placeholder={t['com.streamlain.auth.set.password.placeholder']({
          min: String(passwordLimits.minLength),
        })}
        onChange={onPasswordChange}
        endFix={
          <div className={statusWrapper}>
            {status ? (
              <Tag
                status={status}
                minimum={t[
                  'com.streamlain.auth.set.password.message.minlength'
                ]({
                  min: String(passwordLimits.minLength),
                })}
                maximum={t[
                  'com.streamlain.auth.set.password.message.maxlength'
                ]({
                  max: String(passwordLimits.maxLength),
                })}
              />
            ) : null}
            <IconButton
              className={visibilityToggle}
              size="20"
              type="button"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <InvisibleIcon /> : <ViewIcon />}
            </IconButton>
          </div>
        }
        {...inputProps}
      />
      <Input
        name="confirmPassword"
        className={styles.input}
        type={showConfirmPassword ? 'text' : 'password'}
        size="extraLarge"
        autoComplete="new-password"
        placeholder={t[
          'com.streamlain.auth.set.password.placeholder.confirm'
        ]()}
        onChange={onConfirmPasswordChange}
        endFix={
          <div className={statusWrapper}>
            {confirmStatus ? (
              confirmStatus === 'success' ? (
                <SuccessIcon />
              ) : (
                <ErrorIcon />
              )
            ) : null}
            <IconButton
              className={visibilityToggle}
              size="20"
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              aria-label={
                showConfirmPassword ? 'Hide password' : 'Show password'
              }
            >
              {showConfirmPassword ? <InvisibleIcon /> : <ViewIcon />}
            </IconButton>
          </div>
        }
        {...inputProps}
      />
    </form>
  );
};
